import CatalogEntity from "../Components/CatalogEntity";
import {useEffect, useState} from "react";
import {SelectFieldCellEditor, renderColumnValue} from "../../../General/SharedMethods";
import useHardwareCatalog from "../Hooks/UseHardwareCatalog";
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, LinearProgress, TextField} from "@mui/material";
import {MRT_EditActionButtons} from "material-react-table";
import * as yup from 'yup';

const ActuatorValveCompatibilityEditor = ({enums}) => {
    const [actuatorsOptions, setActuatorsOptions] = useState([]);
    const [valvesOptions, setValvesOptions] = useState([]);
    const {getAll} = useHardwareCatalog();
    const [isLoading, setIsLoading] = useState(true);

    const ViewActuatorValveCompatibilityDialog = (columns, viewRow, setViewRow) => {
        const components = [];
        for (let i = 0; i < columns.length; i += 1) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(1)].map((_, j) => {
                        const column = columns[i + j];
                        return column && (
                            <Grid item xs={12} key={column.accessorKey} minWidth={'40vh'}>
                                <TextField
                                    label={column.header}
                                    defaultValue={renderColumnValue(column, viewRow, enums)}
                                    InputProps={{
                                        readOnly: true,
                                    }}
                                    variant="standard"
                                    fullWidth
                                />
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Dialog maxWidth="xl" open={Boolean(viewRow)} onClose={() => setViewRow(null)}>
                <DialogTitle variant="h4">View Details</DialogTitle>
                <DialogContent>
                    {components}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewRow(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    };

    const ActuatorValveCompatibilityDialog = (table, row, internalEditComponents, validationErrors, setValidationErrors, dialogType) => {
        const components = [];
        for (let i = 1; i < internalEditComponents.length; i += 2) {
            components.push(
                <Grid container spacing={2} marginBottom={1} padding={1} key={`row-${i}`}>
                    {[...Array(2)].map((_, j) => {
                        const component = internalEditComponents[i + j];
                        return component && (
                            <Grid item xs={6} key={`item-${i}-${j}`} minWidth={'40vh'}>
                                {component}
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }
        return (
            <Dialog
                fullWidth
                maxWidth="sm"
                open={true}
            >
                <DialogTitle variant="h4">{dialogType === "edit" ? "Edit Actuator Valve Compatibility" : "Create a new Actuator Valve Compatibility"}</DialogTitle>
                <DialogContent
                    sx={{
                        overflowY: 'auto',
                    }}
                >
                    {components}
                </DialogContent>
                <DialogActions>
                    <MRT_EditActionButtons variant="text" table={table} row={row}/>
                </DialogActions>
            </Dialog>
        );
    }

    const loadActuatorAndValveOptions = async () => {
        const actuatorsPromise = getAll("actuators");
        const valvesPromise = getAll("valves");
        return Promise.all([actuatorsPromise, valvesPromise]);
    }


    useEffect(() => {
        loadActuatorAndValveOptions()
            .then(([actuators, valves]) => {
                setActuatorsOptions(actuators.data.content?.map(actuator => ({label: actuator.name, value: actuator})));
                setValvesOptions(valves.data.content?.map(valve => ({label: valve.name, value: valve})));
                setIsLoading(false);
            }).catch(error => {
            setIsLoading(false);
            console.error(error);

        });
    }, []);

    const createActuatorValveCompatibilityColumns = (validationErrors, setValidationErrors) => {
        return [
            {
                accessorKey: 'id',
                header: 'Id',
                enableEditing: false,
                muiEditTextFieldProps: {
                    type: 'number',
                    hidden: true,
                },
            },
            {
                accessorKey: 'actuator',
                header: 'Actuator',
                filterVariant: 'multi-select',
                filterSelectOptions: actuatorsOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue()?.name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue()?.name,
                    actuatorsOptions,
                    true,
                    setValidationErrors,
                    validationErrors)
            },
            {
                accessorKey: 'valve',
                header: 'Valve',
                filterVariant: 'multi-select',
                filterSelectOptions: valvesOptions.map(option => option.label),
                muiEditTextFieldProps: {
                    type: 'multi-select',
                },
                Cell: ({cell}) => cell.getValue()?.name,
                Edit: ({row, column, cell}) => SelectFieldCellEditor(
                    row,
                    column,
                    cell.getValue()?.name,
                    valvesOptions,
                    true,
                    setValidationErrors,
                    validationErrors)
            },

        ]
    }

    const actuatorValveCompatibilityValidationSchema = yup.object().shape({
        actuator: yup.object().typeError('Actuator is required').required('Actuator is required'),
        valve: yup.object().typeError('Valve is required').required('Valve is required'),
    });

    return isLoading ? <LinearProgress/> : <CatalogEntity entityType={'actuator-valve-compatibilities'}
                                                          entityName={'Actuator Valve Compatibility'}
                                                          createColumns={createActuatorValveCompatibilityColumns}
                                                          validationSchema={actuatorValveCompatibilityValidationSchema}
                                                          customEditDialog={ActuatorValveCompatibilityDialog}
                                                          customCreateDialog={ActuatorValveCompatibilityDialog}
                                                          customViewDialog={ViewActuatorValveCompatibilityDialog}
                                                          enums={enums}
    />
}

export default ActuatorValveCompatibilityEditor;
